import { memo, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { CaptionVideoTasksPanel } from '~/components/CaptionVideoTasks/CaptionVideoTasksPanel';
import { isCaptionVideoTask } from '~/components/CaptionVideoTasks/isCaptionVideoTask';
import { type CaptionVideoTask } from '~/components/CaptionVideoTasks/types';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { removeTasksAction } from '~/store/tasks/actions';
import { makeAssetTransformationTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';

export const PrivateCaptionVideoTasksPanel = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const dispatch = useDispatch();
  const { cancelTask } = useCancelTask();

  const tasksSelector = useMemo(
    () => makeAssetTransformationTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );

  const allTransformationTasks = useAirSelector(tasksSelector);
  const captionVideoTasks = useMemo<CaptionVideoTask[]>(
    () => allTransformationTasks.filter(isCaptionVideoTask),
    [allTransformationTasks],
  );

  useUnmount(() => {
    dispatch(
      removeTasksAction({
        taskIds: captionVideoTasks.map(({ localTaskId }) => localTaskId),
      }),
    );
  });

  if (captionVideoTasks.length < 1) {
    return null;
  }

  return <CaptionVideoTasksPanel tasks={captionVideoTasks} onCancel={cancelTask} onClear={cancelTask} />;
});

PrivateCaptionVideoTasksPanel.displayName = 'PrivateCaptionVideoTasksPanel';
